import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {List} from 'immutable';
import {entityTypeNames} from 'components/GraphingInput/GraphingInput.react';
import {Checkbox, Text, Button, Modal, Dialogue, Chip, Icon} from '@albert-io/atomic';

import {upperFirst} from 'lib/stringUtils';

const entityNamesToDisplayNames = entityTypeNames.reduce((acc, name) => {
  acc[name] = upperFirst(name);
  return acc;
}, {});

const StudentAllowedEntities = ({correctAnswerEntities, allowedEntities, toggleAllowedEntity}) => {
  const requiredEntities = correctAnswerEntities.map(({type}) => type);
  const [showModal, setShowModal] = useState(false);
  const toggleModal = () => setShowModal((show) => !show);

  const sortedEntities = [...new Set([...requiredEntities, ...entityTypeNames])];

  return (
    <div>
      <Text className='u-mar-b_2' as='p' bold>
        Tools available to students:
      </Text>
      <Button className='u-mar-r_1' onClick={toggleModal}>
        Select tools
      </Button>
      {sortedEntities
        .filter((name) => allowedEntities.includes(name))
        .map((name) => {
          const entityName = entityNamesToDisplayNames[name];
          const isRequired = requiredEntities.includes(name);
          if (isRequired) {
            return (
              <Chip key={name} color='blue'>
                {entityName}
              </Chip>
            );
          }
          return (
            <Chip
              key={name}
              color='blue'
              as='button'
              hasHover
              onClick={() => toggleAllowedEntity(name)}
            >
              {entityName} <Icon className='u-mar-l_1' icon='times' />
            </Chip>
          );
        })}
      {showModal && (
        <Modal ariaLabel='Student graphing tool options' handleClose={toggleModal}>
          {({CloseButtonWrapper, modalContentStyle}) => {
            return (
              <Dialogue
                inModal
                hideCloseBtn
                size='m'
                className={modalContentStyle}
                handleClose={toggleModal}
                title='Student graphing tools'
              >
                <Dialogue.Body>
                  {sortedEntities.map((name) => (
                    <label key={name} className='u-display_flex u-align-items_center u-pad-tb_1'>
                      <Checkbox
                        checked={allowedEntities.includes(name)}
                        disabled={requiredEntities.includes(name)}
                        onChange={() => toggleAllowedEntity(name)}
                      />
                      <Text bold emphasis='medium' className='u-mar-l_1'>
                        Add {entityNamesToDisplayNames[name]}
                      </Text>
                    </label>
                  ))}
                </Dialogue.Body>
                <Dialogue.BtnGroup>
                  <CloseButtonWrapper>
                    <Button>Done</Button>
                  </CloseButtonWrapper>
                </Dialogue.BtnGroup>
              </Dialogue>
            );
          }}
        </Modal>
      )}
    </div>
  );
};

StudentAllowedEntities.propTypes = {
  allowedEntities: PropTypes.array.isRequired,
  correctAnswerEntities: PropTypes.array.isRequired,
  toggleAllowedEntity: PropTypes.func.isRequired
};

export function useAllowedEntitiesState(initialQuestion, correctAnswerEntities) {
  const initialAllowedEntities = initialQuestion
    .getQuestionTypeAttributes()
    .getIn(['graph_contains', 'allowed_entities'], List())
    .toJS();

  const [selectedEntities, setSelectedEntities] = useState(new Set(initialAllowedEntities));

  const boardEntities = new Set(correctAnswerEntities.map(({type}) => type));

  const toggleAllowedEntity = (name) => {
    const hasEntity = selectedEntities.has(name);
    setSelectedEntities((state) => {
      const tempState = new Set([...state]);
      tempState[hasEntity ? 'delete' : 'add'](name);
      return tempState;
    });
  };

  const allowedEntities = [...mergeSets(selectedEntities, boardEntities)];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoedAllowedEntities = useMemo(() => allowedEntities, [allowedEntities.length]);

  return [memoedAllowedEntities, toggleAllowedEntity];
}

function mergeSets(...sets) {
  return sets.reduce((acc, curr) => new Set([...acc, ...curr]));
}

export default StudentAllowedEntities;
